import { useRef, useState } from "react";
import { faCheckCircle } from "@/icons/regular-icons";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import useAPI from "@/hooks/useAPI";
import useLoader from "@/hooks/useLoader";
import {
  type IForgotPasswordAlertState,
  type IForgotPasswordError,
  type ILoginModel,
  LoginSection,
} from "@/components/screens/login/types/login";

const LoginViewModel = (): ILoginModel => {
  const router = useRouter();
  const { query } = router;
  const { authService } = useAPI();
  const { showLoading, hideLoading } = useLoader();
  const usernameRef = useRef<HTMLInputElement>(undefined);
  const passwordRef = useRef<HTMLInputElement>(undefined);
  const [currentSection, setCurrentSection] = useState<LoginSection>(
    LoginSection.Login,
  );
  const [forgotPasswordAlert, setForgotPasswordAlert] =
    useState<IForgotPasswordAlertState>({
      success: false,
      showAlert: false,
    });
  const [forgotPasswordError, setForgotPasswordError] =
    useState<IForgotPasswordError>({
      hasForgotPasswordError: false,
      error: null,
    });

  const OTPRef = useRef<HTMLInputElement>(undefined);

  const forgotPassword = async () => {
    showLoading();

    const { success, error } = await authService.forgotPassword(
      usernameRef.current.value,
    );

    if (success) {
      setForgotPasswordAlert({
        success: true,
        showAlert: true,
      });

      setForgotPasswordError({ hasForgotPasswordError: false, error });
      setCurrentSection(LoginSection.Login);
    } else {
      setForgotPasswordError({ hasForgotPasswordError: true, error });
    }

    hideLoading();
  };

  const loginButtonOnClick = (e) => {
    e.preventDefault();
    if (currentSection === LoginSection.Login) {
      signIn("dines-credentials", {
        username: usernameRef.current.value,
        password: passwordRef.current.value,
      });
    } else {
      forgotPassword();
    }
  };

  const onShowOTPSection = () => {
    setCurrentSection(LoginSection.OTP);
  };

  const OTPLoginButtonClick = (e) => {
    e.preventDefault();
    signIn("dines-otp", {
      code: OTPRef.current.value,
    });
  };

  const onBackButtonHandler = () => {
    setForgotPasswordError({ hasForgotPasswordError: false, error: "" });
    setCurrentSection(LoginSection.Login);
  };

  const loginTitle = () => {
    if (
      currentSection === LoginSection.Login &&
      !forgotPasswordAlert.showAlert
    ) {
      return "Organisation Login";
    }

    if (
      currentSection === LoginSection.Login &&
      forgotPasswordAlert.showAlert
    ) {
      return "Password Reset";
    }

    if (currentSection === LoginSection.OTP) {
      return "One Time Password";
    }

    return "Forgot Password";
  };

  const subtitles = [
    {
      key: "initial",
      title: "D",
      onClick: () => {
        setCurrentSection(LoginSection.OTP);
      },
    },
    {
      key: "rest",
      title: "ines Hospitality Operating System",
    },
  ];

  setTimeout(() => {
    if (forgotPasswordAlert.success) {
      setForgotPasswordAlert({
        success: true,
        showAlert: false,
      });
    }
  }, 4000);

  return {
    forgotPasswordAlert,
    labels: {
      title: loginTitle(),
      subtitles,
    },

    OTPViewModel: {
      labels: {
        title: "Please enter your OTP code",
        codePlaceholder: "Code",
        backButtonText: "Back",
        loginButtonText: "Login",
      },
      OTPRef,
      OTPLoginButtonClick,
      showOTPSection: currentSection === LoginSection.OTP,
      onBackButtonHandler,
      onShowOTPSection: onShowOTPSection,
    },

    // Login Form Inputs View Model
    formInputsViewModel: {
      labels: {
        title:
          currentSection === LoginSection.Login
            ? ""
            : "Please enter your username",
      },
      query,
      currentSection,
      usernameRef,
      passwordRef,
      setCurrentSection,
      forgotPasswordError,
    },

    // Forgot Password Alert View Model
    forgotPasswordAlertViewModel: {
      labels: {
        alertTitle: "A link has been sent via email to reset your password",
      },
      classNames: {
        containerClassNames: "text-dines-green",
      },
      icon: faCheckCircle,
      forgotPassword,
      dataActive: forgotPasswordAlert.success,
    },

    // Actions View Model
    actionsViewModel: {
      labels: {
        loginButtonTitle:
          currentSection === LoginSection.Login ? "Login" : "Reset Password",
      },
      currentSection,
      usernameRef,
      passwordRef,
      forgotPassword,
      loginButtonOnClick,
      onBackButtonHandler,
    },

    footerViewModel: {
      labels: {
        needHelpText: "Need help?",
        teamText: "Dines support team",
        getInTouchText: "Get in touch with the ",
        chatToUsText: "Chat to us live",
        issuesText: "if you have any issues with your account.",
        supportLink: "mailto:care@dines.co.uk",
        chatLink: "https://dines.co.uk/chat",
        orText: "or ",
      },
    },
  };
};

export default LoginViewModel;
